/**
 * @itoresearch/api-client — typed client for the Ito terminal API.
 *
 * Everything under `src/generated/` is produced by `@hey-api/openapi-ts` from the terminal
 * OpenAPI document (`crates/api` `emit-openapi`). This barrel re-exports the generated
 * surface and adds a configured `client` bound to the API base URL.
 */

export * from "./generated/types.gen";
export * from "./generated/zod.gen";
export * from "./generated/sdk.gen";
export * from "./generated/@tanstack/react-query.gen";

export {
  createClient,
  type Client,
  type ClientOptions,
  type Config,
  type CreateClientConfig,
  type Options,
} from "./generated/client";

import {
  createClient as createTypedClient,
  type Client,
} from "./generated/client";
import type { ClientOptions as GeneratedClientOptions } from "./generated/types.gen";

/**
 * Create a client bound to `baseUrl`. In the web app this is `VITE_API_BASE_URL`
 * (or relative "", so the Vite dev proxy handles it); Tauri/Capacitor set it to
 * the API origin.
 *
 * The client always sends credentials: browser auth is the API's sealed session
 * cookie (set by `/auth/callback`, scoped to the registrable domain), so XHR
 * must carry it cross-origin — the API's CORS allowlist answers with
 * `Access-Control-Allow-Credentials: true`.
 */
export function createApiClient(baseUrl: string) {
  const client = createTypedClient({
    baseUrl: baseUrl as GeneratedClientOptions["baseUrl"],
  });
  client.setConfig({
    fetch: ((input: RequestInfo | URL, init?: RequestInit) =>
      fetch(input, {
        ...init,
        credentials: "include",
      })) as typeof fetch,
  });
  return client;
}

import { zPageManifest } from "./generated/zod.gen";
import type { PageManifest } from "./generated/types.gen";

/**
 * Validate a `PageManifest` at the boundary before the Page Runtime builds its graph. Throws
 * on invalid manifests (surfaced as structured zod errors).
 */
export function validatePageManifest(value: unknown): PageManifest {
  return zPageManifest.parse(value) as PageManifest;
}

/**
 * Register a request interceptor on a `client` that attaches `Authorization: Bearer <token>`.
 *
 * Dev-mode workaround: the API accepts `Bearer dev.<user>[.<org>[.<role>]]` tokens without
 * signature checks when `ITO_AUTH_DEV_MODE` is enabled (`crates/api/src/auth.rs`). This lets
 * the terminal prototype run locally without the WorkOS sign-in flow.
 */
export function withBearerToken(client: Client, token: string): () => void {
  const id = client.interceptors.request.use((request: Request) => {
    request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  });
  return () => client.interceptors.request.eject(id);
}

/** Shorthand for the dev-mode token (`dev.<user>.<org>`). */
export function devToken(user = "user", org = "org"): string {
  return `dev.${user}.${org}`;
}
